<template>
  <div class="flex flex-col items-center justify-center py-24">
    <!-- Spinner with soft backdrop -->
    <div class="rounded-full bg-indigo-50 p-5 animate-breathing">
      <SpinnerIcon size="h-10 w-10" color="text-indigo-600" />
    </div>

    <!-- Cycling message -->
    <div class="mt-6 h-7 flex items-center justify-center overflow-hidden">
      <Transition name="message-slide" mode="out-in">
        <p
          :key="currentMessageIndex"
          class="text-base font-medium tracking-tight text-gray-900 text-center"
        >
          {{ messages[currentMessageIndex] }}
        </p>
      </Transition>
    </div>

    <!-- Pulsing dots -->
    <div class="mt-4 flex items-center gap-1.5">
      <span
        v-for="i in 3"
        :key="i"
        class="h-1.5 w-1.5 rounded-full bg-indigo-300 animate-dot-pulse"
        :style="{ animationDelay: `${(i - 1) * 200}ms` }"
      />
    </div>

    <!-- Subtitle -->
    <p v-if="subtitle" class="text-sm text-gray-400 mt-3 tracking-wide">{{ subtitle }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import SpinnerIcon from "@/components/ui/SpinnerIcon.vue";

const props = defineProps({
  messages: { type: Array, required: true },
  subtitle: { type: String, default: "" },
});

const currentMessageIndex = ref(0);
let interval = null;

onMounted(() => {
  interval = setInterval(() => {
    currentMessageIndex.value = (currentMessageIndex.value + 1) % props.messages.length;
  }, 2000);
});

onUnmounted(() => {
  if (interval) clearInterval(interval);
});
</script>

<style scoped>
/* Message slide transition */
.message-slide-enter-active,
.message-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.message-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.message-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Breathing pulse on spinner container */
@keyframes breathing {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.85; }
}
.animate-breathing {
  animation: breathing 3s ease-in-out infinite;
}

/* Sequential dot pulse */
@keyframes dot-pulse {
  0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
  30% { opacity: 1; transform: scale(1.2); }
}
.animate-dot-pulse {
  animation: dot-pulse 1.4s ease-in-out infinite;
}
</style>
